import React, { useEffect, useState } from 'react'
import SearchableSelect from '../components/SearchableSelect'
import { BreathAPIs } from '../APIs/BreathAPIs'
import styles from './LocationLookup.module.css'

// Required field, shown alongside email/phone on the same guest-login view.
// Three cascading, searchable selectors - State, City, then the matching
// Location itself. Each level is fetched only once it's needed (states on
// mount, cities once a state is picked, locations once a city is picked)
// instead of pulling every guest-accessible location down up front.
const LocationLookup = ({ selectedLocationId, onSelectLocation }) => {
  const [states, setStates] = useState([])
  const [isLoadingStates, setIsLoadingStates] = useState(true)

  const [cities, setCities] = useState([])
  const [isLoadingCities, setIsLoadingCities] = useState(false)

  const [matchingLocations, setMatchingLocations] = useState([])
  const [isLoadingLocations, setIsLoadingLocations] = useState(false)

  const [selectedState, setSelectedState] = useState('')
  const [selectedCity, setSelectedCity] = useState('')

  useEffect(() => {
    BreathAPIs.getPublicGuestLocations()
      .then(response => {
        setStates(Array.isArray(response?.data?.states) ? response.data.states : [])
      })
      .catch(() => setStates([]))
      .finally(() => setIsLoadingStates(false))
  }, [])

  useEffect(() => {
    if (!selectedState) {
      setCities([])
      return
    }
    setIsLoadingCities(true)
    BreathAPIs.getPublicGuestLocations(selectedState)
      .then(response => {
        setCities(Array.isArray(response?.data?.cities) ? response.data.cities : [])
      })
      .catch(() => setCities([]))
      .finally(() => setIsLoadingCities(false))
  }, [selectedState])

  useEffect(() => {
    if (!selectedState || !selectedCity) {
      setMatchingLocations([])
      return
    }
    setIsLoadingLocations(true)
    BreathAPIs.getPublicGuestLocations(selectedState, selectedCity)
      .then(response => {
        setMatchingLocations(Array.isArray(response?.data) ? response.data : [])
      })
      .catch(() => setMatchingLocations([]))
      .finally(() => setIsLoadingLocations(false))
  }, [selectedState, selectedCity])

  return (
    <div className={styles.wrapper}>
      <div className={styles.selectGroup}>
        <SearchableSelect
          value={selectedState}
          label="State"
          disabled={isLoadingStates}
          placeholder={isLoadingStates ? 'Loading...' : 'Select State'}
          onChange={(val) => {
            setSelectedState(val)
            setSelectedCity('')
            onSelectLocation(null)
          }}
          options={states.map(state => ({ value: state, label: state }))}
          notFoundText="No states found"
        />
      </div>
      <div className={styles.selectGroup}>
        <SearchableSelect
          value={selectedCity}
          label="City"
          disabled={!selectedState || isLoadingCities}
          placeholder={isLoadingCities ? 'Loading...' : 'Select City'}
          onChange={(val) => {
            setSelectedCity(val)
            onSelectLocation(null)
          }}
          options={cities.map(city => ({ value: city, label: city }))}
          notFoundText="No cities found"
        />
      </div>

      {selectedState && selectedCity && (
        <div className={styles.selectGroup}>
          <SearchableSelect
            value={selectedLocationId || ''}
            label="Location"
            disabled={isLoadingLocations || matchingLocations.length === 0}
            placeholder={
              isLoadingLocations
                ? 'Loading...'
                : matchingLocations.length === 0
                  ? 'No locations available'
                  : 'Select Location'
            }
            onChange={(val) => {
              const loc = matchingLocations.find(l => l._id === val)
              if (loc) onSelectLocation(loc)
            }}
            options={matchingLocations.map(loc => ({
              value: loc._id,
              label: loc.locationName,
              badge: `${loc.availableChargerCount ?? 0} available`
            }))}
            notFoundText="No locations found"
          />
        </div>
      )}
    </div>
  )
}

export default LocationLookup
